import _ from 'lodash';

import changeComposition from 'actions/composition/lib/change_composition';
import Composition from 'models/composition';
import CurrentCompositionUpdater from '../lib/current_composition_updater';
import CustomerView from 'models/location/customer_view';
import getComposition from 'actions/customer/lib/get_composition';
import getCompositionsStore from 'actions/customer/lib/get_compositions_store';
import qconsole from 'scripts/lib/qconsole';
import ConversationItemType from 'models/conversation_item_type';

let uploadsToDelete = {};

export default class CompositionGatewayObserver {
  constructor(context) {
    this.context = context;
    this.currentCompositionUpdater = new CurrentCompositionUpdater(context);
  }

  getStore(customerId) {
    return getCompositionsStore(this.context, customerId);
  }

  handleFetchCompositions(compositionDtos, customerId) {
    const store = this.getStore(customerId);
    if (!store) return;

    const hasExternalAgentActions = this.context.stores.appFeatures.get().isEnabled('externalAgentActions');
    let sortedCompositions = _(compositionDtos)
      .map(c => ({ ...c, persisted: true }))
      .map(Composition.fromJs.bind(Composition))
      .map(fetched => {
        let existing = store.findBy({ id: fetched.id });
        return existing && existing._version > fetched._version ? existing : fetched;
      })
      .sortBy(composition => Date.parse(composition.updatedAt))
      .value();
    if (!hasExternalAgentActions) {
      sortedCompositions = _.filter(sortedCompositions, composition =>
        this._isAllowedCompositionTaskUser(composition.contentType())
      );
    }

    sortedCompositions = this._removeDuplicateCompositionTypes(sortedCompositions, customerId);

    store.set(sortedCompositions);
    store.resetLoading();

    this.currentCompositionUpdater.update();
  }

  _isAllowedCompositionTaskUser(contentType) {
    return contentType === ConversationItemType.TASK || contentType === ConversationItemType.CONVERSATION_NOTE;
  }

  _removeDuplicateCompositionTypes(sortedCompositions, customerId) {
    let agentId = this.context.stores.currentAgent.get().id;

    const [remaining, duplicates] = _.partition(sortedCompositions, currentComposition => {
      const isDuplicateCompositionType = _.some(sortedCompositions, c => {
        return (
          c.contentType() === currentComposition.contentType() &&
          c.id !== currentComposition.id &&
          new Date(c.updatedAt) > new Date(currentComposition.updatedAt)
        );
      });
      if (isDuplicateCompositionType) {
        return false;
      }
      return true;
    });

    _.forEach(duplicates, composition => {
      this.context.gateways.composition.remove(agentId, customerId, composition.id);
    });

    return remaining;
  }

  handleFetchError(errorDto) {
    qconsole.log(`Could not fetch compositions ${JSON.stringify(errorDto)}`);
  }

  handleComposition(compositionDto) {
    let composition = Composition.fromJs(compositionDto);
    const compositionsStore = getCompositionsStore(this.context, composition.customerId);

    // Don't do anything if the customer isn't in the store
    if (!compositionsStore) {
      return;
    }
    const existingComposition = getComposition(this.context, composition.customerId, composition.id);

    // Don't do anything if we receive an out of date updated composition
    if (existingComposition && composition._version <= existingComposition._version) {
      return;
    }

    // No reason to handle composition if we're not on the correct customer page
    let currentLocation = this.context.stores.currentLocation.get();
    let currentComposition =
      currentLocation.currentCompositionId && compositionsStore.findBy({ id: currentLocation.currentCompositionId });
    if (currentComposition && currentComposition.content.constructor === composition.content.constructor) {
      return;
    }

    // When we save compositions, we don't include incomplete uploads (because if we load the composition from another window,
    // we won't have the File object available). But we also don't want to lose incomplete attachments for our current compositions
    // when we save. So we "merge" the incomplete attachments from the local composition back into the received composition.
    if (existingComposition) {
      composition.mergeIncompleteUploads(existingComposition.getUploads());
      this.getStore(currentLocation.customerId).addOrReplace(composition);
    }
  }

  handleDelete(compositionId) {
    const store = getCompositionsStore(this.context);
    let composition = store.findBy({ id: compositionId });
    if (composition) {
      store.remove(compositionId);

      let currentLocation = this.context.stores.currentLocation.get();
      if (
        currentLocation instanceof CustomerView &&
        currentLocation.isInComposition(composition.customerId, compositionId)
      ) {
        let otherCustomerCompositions = store.findAll();
        if (otherCustomerCompositions.length) {
          let nextComposition = _.last(
            _.sortBy(otherCustomerCompositions, comp => comp.updatedAt && Date.parse(comp.updatedAt))
          );
          changeComposition(this.context, nextComposition);
        } else {
          changeComposition(this.context, null);
        }
      }
    }
  }

  handleDeleteError({ agentId, compositionId }, errors, correlationId) {
    this.constructor.resetUploadsToDelete(correlationId);
  }

  handleDeleteSuccess({ agentId, compositionId }, correlationId) {
    this.removeUploadsForRequest(correlationId);
  }

  handleUpdateErrors({ agentId, compositionId }, errors, correlationId) {
    this.constructor.resetUploadsToDelete(correlationId);
  }

  handleUpdateSuccess({ compositionId, customerId }, correlationId) {
    this.removeUploadsForRequest(correlationId);
    this.context.stores.localCompositions.remove(customerId, compositionId);
  }

  handleAddSuccess({ agentId }, correlationId) {
    return;
  }

  handleAddError({ agentId }, correlationId) {
    return;
  }

  removeUploadsForRequest(correlationId) {
    let compositionUploads = uploadsToDelete[correlationId];
    if (compositionUploads) {
      this.constructor.resetUploadsToDelete(correlationId);
      this.removeUploads(compositionUploads);
    }
  }

  removeUploads(compositionUploads) {
    compositionUploads.uploads.forEach(u =>
      this.context.gateways.compositionUpload.remove(compositionUploads.agentId, compositionUploads.getUploadPath(u.id))
    );
  }

  static resetUploadsToDelete(correlationId) {
    delete uploadsToDelete[correlationId];
  }

  static setUploadsToDelete(correlationId, compositionUploads) {
    uploadsToDelete[correlationId] = compositionUploads;
  }
}



// WEBPACK FOOTER //
// ./src/scripts/application/actions/composition/composition_gateway_observer.js